import { cn, formatDate, formatDateTime } from '@berky/ui'
import { CalendarClock, CheckCircle2, CircleDot } from 'lucide-react'

import type { FeedbackActivity, FeedbackEntry, FeedbackStatus } from '../types'
import { FeedbackStatusBadge } from './feedback-status'

export function FeedbackHistory({
  entry,
  activities,
  currentUserId,
}: {
  entry: FeedbackEntry
  activities: FeedbackActivity[]
  currentUserId: string
}) {
  const eigeneRueckmeldung = entry.erstellerId === currentUserId

  return (
    <ol className="space-y-4" aria-label="Gesprächsverlauf">
      <li className={cn('flex', eigeneRueckmeldung ? 'justify-end' : 'justify-start')}>
        <article
          aria-label={
            eigeneRueckmeldung
              ? 'Eigenes ursprüngliches Feedback'
              : `Ursprüngliches Feedback von ${entry.erstellerName}`
          }
          className={nachrichtenKlassen(eigeneRueckmeldung)}
        >
          <div className={cn('mb-1', metadatenKlassen(eigeneRueckmeldung))}>
            <span className="font-medium">
              {eigeneRueckmeldung ? 'Sie' : entry.erstellerName}
            </span>
            <time dateTime={entry.angelegtAm}>{formatDateTime(entry.angelegtAm)}</time>
          </div>
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{entry.text}</p>
        </article>
      </li>

      {activities.map((activity, index) => {
        const vorherige = activities[index - 1]
        const naechste = activities[index + 1]

        if (istStatusVonKombinierterAktion(activity, vorherige)) return null
        if (istBegonneneBearbeitung(activity, naechste)) {
          return (
            <Bearbeitungsmeldung
              key={activity.id}
              activity={activity}
              currentUserId={currentUserId}
            />
          )
        }
        if (istAblehnung(activity, naechste)) {
          return (
            <Ablehnungsmeldung
              key={activity.id}
              activity={activity}
              currentUserId={currentUserId}
            />
          )
        }
        if (istZurueckstellung(activity, naechste)) {
          return (
            <Zurueckstellungsmeldung
              key={activity.id}
              activity={activity}
              currentUserId={currentUserId}
            />
          )
        }
        if (istErledigteUmsetzung(activity, naechste)) {
          return (
            <Umsetzungsmeldung
              key={activity.id}
              activity={activity}
              currentUserId={currentUserId}
              erledigt
            />
          )
        }

        return (
          <Aktivitaet
            key={activity.id}
            activity={activity}
            currentUserId={currentUserId}
          />
        )
      })}
    </ol>
  )
}

function Aktivitaet({
  activity,
  currentUserId,
}: {
  activity: FeedbackActivity
  currentUserId: string
}) {
  if (activity.art === 'Kommentar') {
    const eigeneNachricht = activity.bearbeiterId === currentUserId
    return (
      <li className={cn('flex', eigeneNachricht ? 'justify-end' : 'justify-start')}>
        <article
          aria-label={
            eigeneNachricht
              ? 'Eigene Nachricht'
              : `Nachricht von ${activity.bearbeiterName}`
          }
          className={nachrichtenKlassen(eigeneNachricht)}
        >
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{activity.text}</p>
          <div className={cn('mt-2', metadatenKlassen(eigeneNachricht))}>
            <span>{eigeneNachricht ? 'Sie' : activity.bearbeiterName}</span>
            <time dateTime={activity.angelegtAm}>
              {formatDateTime(activity.angelegtAm)}
            </time>
          </div>
        </article>
      </li>
    )
  }

  if (activity.art === 'Statusänderung') {
    return <Statusmeldung activity={activity} />
  }

  if (activity.art === 'Bereichsänderung') {
    return <Bereichsmeldung activity={activity} />
  }

  if (activity.art === 'Zurückstellung') {
    return <Zurueckstellungsmeldung activity={activity} currentUserId={currentUserId} />
  }

  return <Umsetzungsmeldung activity={activity} currentUserId={currentUserId} />
}

function Bereichsmeldung({ activity }: { activity: FeedbackActivity }) {
  return (
    <li className="flex justify-center">
      <p className="inline-flex max-w-full flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full bg-muted px-3 py-1.5 text-center text-xs text-muted-foreground">
        <span>{activity.text}</span>
        <time dateTime={activity.angelegtAm}>{formatDateTime(activity.angelegtAm)}</time>
      </p>
    </li>
  )
}

function Statusmeldung({ activity }: { activity: FeedbackActivity }) {
  const erledigt = activity.neuerStatus === 'Erledigt'
  const automatischWiedervorgelegt =
    activity.bearbeiterId === 'feedback-wiedervorlage' && activity.neuerStatus === 'Neu'
  const Icon = erledigt ? CheckCircle2 : CircleDot

  return (
    <li className="flex justify-center">
      <div
        className={cn(
          'w-full max-w-2xl rounded-lg border px-4 py-3',
          statusKlassen(activity.neuerStatus),
        )}
      >
        <div className="flex items-start gap-3">
          <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              {automatischWiedervorgelegt
                ? 'Automatisch wiedervorgelegt'
                : erledigt
                  ? 'Als erledigt markiert'
                  : 'Status geändert'}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
              <span>
                {automatischWiedervorgelegt
                  ? 'Das Feedback ist wieder zur Bearbeitung fällig:'
                  : `${activity.bearbeiterName} hat den Status gesetzt:`}
              </span>
              {activity.neuerStatus && (
                <FeedbackStatusBadge status={activity.neuerStatus} />
              )}
            </div>
          </div>
          <time
            className="shrink-0 text-xs text-muted-foreground"
            dateTime={activity.angelegtAm}
          >
            {formatDateTime(activity.angelegtAm)}
          </time>
        </div>
      </div>
    </li>
  )
}

function Zurueckstellungsmeldung({
  activity,
  currentUserId,
}: {
  activity: FeedbackActivity
  currentUserId: string
}) {
  const eigeneNachricht = activity.bearbeiterId === currentUserId

  return (
    <li className={cn('flex', eigeneNachricht ? 'justify-end' : 'justify-start')}>
      <article
        aria-label={
          eigeneNachricht
            ? 'Eigene Zurückstellung'
            : `Zurückstellung von ${activity.bearbeiterName}`
        }
        className={cn(
          'max-w-[85%] rounded-2xl border bg-neutral-surface px-4 py-3 text-neutral-strong shadow-sm',
          eigeneNachricht ? 'rounded-br-sm' : 'rounded-bl-sm',
        )}
      >
        <div className="flex items-start gap-3">
          <CalendarClock className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">Feedback zurückgestellt</p>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {activity.text}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {eigeneNachricht ? 'Sie' : activity.bearbeiterName}
              {activity.wiedervorlageAm &&
                ` · Wiedervorlage am ${formatDate(activity.wiedervorlageAm)}`}
            </p>
          </div>
          <time
            className="shrink-0 text-xs text-muted-foreground"
            dateTime={activity.angelegtAm}
          >
            {formatDateTime(activity.angelegtAm)}
          </time>
        </div>
      </article>
    </li>
  )
}

function Umsetzungsmeldung({
  activity,
  currentUserId,
  erledigt = false,
}: {
  activity: FeedbackActivity
  currentUserId: string
  erledigt?: boolean
}) {
  const eigeneNachricht = activity.bearbeiterId === currentUserId

  return (
    <li className={cn('flex', eigeneNachricht ? 'justify-end' : 'justify-start')}>
      <article
        aria-label={
          eigeneNachricht
            ? 'Eigene Fertigmeldung'
            : `Fertigmeldung von ${activity.bearbeiterName}`
        }
        className={cn(
          'max-w-[85%] rounded-2xl border border-success bg-success-surface px-4 py-3 text-success-strong shadow-sm',
          eigeneNachricht ? 'rounded-br-sm' : 'rounded-bl-sm',
        )}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              {erledigt ? 'Umsetzung abgeschlossen' : 'Umsetzung gemeldet'}
            </p>
            {activity.text && (
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {activity.text}
              </p>
            )}
            <p className="mt-2 text-xs text-muted-foreground">
              {eigeneNachricht ? 'Sie' : activity.bearbeiterName}
              {erledigt && ' · Status: Erledigt'}
            </p>
          </div>
          <time
            className="shrink-0 text-xs text-muted-foreground"
            dateTime={activity.angelegtAm}
          >
            {formatDateTime(activity.angelegtAm)}
          </time>
        </div>
      </article>
    </li>
  )
}

function Ablehnungsmeldung({
  activity,
  currentUserId,
}: {
  activity: FeedbackActivity
  currentUserId: string
}) {
  const eigeneNachricht = activity.bearbeiterId === currentUserId

  return (
    <li className={cn('flex', eigeneNachricht ? 'justify-end' : 'justify-start')}>
      <article
        aria-label={
          eigeneNachricht
            ? 'Eigene Ablehnung'
            : `Ablehnung von ${activity.bearbeiterName}`
        }
        className={cn(
          'max-w-[85%] rounded-2xl border border-destructive bg-destructive-surface px-4 py-3 text-destructive-strong shadow-sm',
          eigeneNachricht ? 'rounded-br-sm' : 'rounded-bl-sm',
        )}
      >
        <div className="flex items-start gap-3">
          <CircleDot className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">Feedback abgelehnt</p>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {activity.text}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {eigeneNachricht ? 'Sie' : activity.bearbeiterName} · Status: Abgelehnt
            </p>
          </div>
          <time
            className="shrink-0 text-xs text-muted-foreground"
            dateTime={activity.angelegtAm}
          >
            {formatDateTime(activity.angelegtAm)}
          </time>
        </div>
      </article>
    </li>
  )
}

function Bearbeitungsmeldung({
  activity,
  currentUserId,
}: {
  activity: FeedbackActivity
  currentUserId: string
}) {
  const eigeneNachricht = activity.bearbeiterId === currentUserId

  return (
    <li className={cn('flex', eigeneNachricht ? 'justify-end' : 'justify-start')}>
      <article
        aria-label={
          eigeneNachricht
            ? 'Eigener Bearbeitungsstart'
            : `Bearbeitungsstart von ${activity.bearbeiterName}`
        }
        className={cn(
          'max-w-[85%] rounded-2xl border border-warning bg-warning-surface px-4 py-3 text-warning-strong shadow-sm',
          eigeneNachricht ? 'rounded-br-sm' : 'rounded-bl-sm',
        )}
      >
        <div className="flex items-start gap-3">
          <CircleDot className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">Bearbeitung gestartet</p>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {activity.text}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              {eigeneNachricht ? 'Sie' : activity.bearbeiterName} · Status: In Bearbeitung
            </p>
          </div>
          <time
            className="shrink-0 text-xs text-muted-foreground"
            dateTime={activity.angelegtAm}
          >
            {formatDateTime(activity.angelegtAm)}
          </time>
        </div>
      </article>
    </li>
  )
}

function istBegonneneBearbeitung(
  activity: FeedbackActivity,
  naechste: FeedbackActivity | undefined,
) {
  return (
    activity.art === 'Kommentar' &&
    naechste?.art === 'Statusänderung' &&
    naechste.neuerStatus === 'In Bearbeitung' &&
    naechste.bearbeiterId === activity.bearbeiterId
  )
}

function istAblehnung(
  activity: FeedbackActivity,
  naechste: FeedbackActivity | undefined,
) {
  return (
    activity.art === 'Kommentar' &&
    naechste?.art === 'Statusänderung' &&
    naechste.neuerStatus === 'Abgelehnt' &&
    naechste.bearbeiterId === activity.bearbeiterId
  )
}

function istErledigteUmsetzung(
  activity: FeedbackActivity,
  naechste: FeedbackActivity | undefined,
) {
  return (
    activity.art === 'Umsetzung' &&
    naechste?.art === 'Statusänderung' &&
    naechste.neuerStatus === 'Erledigt' &&
    naechste.bearbeiterId === activity.bearbeiterId
  )
}

function istZurueckstellung(
  activity: FeedbackActivity,
  naechste: FeedbackActivity | undefined,
) {
  return (
    activity.art === 'Zurückstellung' &&
    naechste?.art === 'Statusänderung' &&
    naechste.neuerStatus === 'Zurückgestellt' &&
    naechste.bearbeiterId === activity.bearbeiterId
  )
}

function istStatusVonKombinierterAktion(
  activity: FeedbackActivity,
  vorherige: FeedbackActivity | undefined,
) {
  if (activity.art !== 'Statusänderung' || !vorherige) return false

  return (
    activity.bearbeiterId === vorherige.bearbeiterId &&
    ((activity.neuerStatus === 'In Bearbeitung' && vorherige.art === 'Kommentar') ||
      (activity.neuerStatus === 'Erledigt' && vorherige.art === 'Umsetzung') ||
      (activity.neuerStatus === 'Abgelehnt' && vorherige.art === 'Kommentar') ||
      (activity.neuerStatus === 'Zurückgestellt' && vorherige.art === 'Zurückstellung'))
  )
}

function statusKlassen(status: FeedbackStatus | null) {
  if (status === 'Erledigt')
    return 'border-success bg-success-surface text-success-strong'
  if (status === 'Abgelehnt') {
    return 'border-destructive bg-destructive-surface text-destructive-strong'
  }
  if (status === 'In Bearbeitung') {
    return 'border-warning bg-warning-surface text-warning-strong'
  }
  if (status === 'Zurückgestellt') {
    return 'border-border bg-neutral-surface text-neutral-strong'
  }
  return 'border-info bg-info-surface text-info-strong'
}

function nachrichtenKlassen(eigeneNachricht: boolean) {
  return cn(
    'max-w-[85%] rounded-2xl px-4 py-3 shadow-sm',
    eigeneNachricht
      ? 'rounded-br-sm bg-primary text-primary-foreground'
      : 'rounded-bl-sm border bg-card text-card-foreground',
  )
}

function metadatenKlassen(eigeneNachricht: boolean) {
  return cn(
    'flex flex-wrap gap-x-3 gap-y-1 text-xs',
    eigeneNachricht ? 'justify-end text-primary-foreground/75' : 'text-muted-foreground',
  )
}
